import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'

type Props = {
  accept?: string
  maxSizeBytes?: number
  onFile: (file: File) => void
  label?: string
  sublabel?: string
}

const DEFAULT_MAX_SIZE = 10 * 1024 * 1024 // 10MB

function validate(file: File, accept: string | undefined, maxSizeBytes: number): string | null {
  if (file.size > maxSizeBytes) {
    const mb = (maxSizeBytes / 1024 / 1024).toFixed(0)
    return `ファイルサイズは ${mb}MB 以内にしてください（現在: ${(file.size / 1024 / 1024).toFixed(1)}MB）`
  }

  if (accept) {
    const allowedExts = accept
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.startsWith('.'))
      .map((s) => s.toLowerCase())

    if (allowedExts.length > 0) {
      const fileExt = '.' + (file.name.split('.').pop() ?? '').toLowerCase()
      if (!allowedExts.includes(fileExt)) {
        return `対応しているファイル形式: ${allowedExts.join(', ')}`
      }
    }
  }

  return null
}

export default function FileDropZone({
  accept,
  maxSizeBytes = DEFAULT_MAX_SIZE,
  onFile,
  label = 'ファイルを選択またはドロップ',
  sublabel = 'タップして選択、またはここにドロップ',
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragOver, setIsDragOver] = useState(false)

  const handleFile = (file: File) => {
    const error = validate(file, accept, maxSizeBytes)
    if (error) {
      toast.error(error)
      return
    }
    onFile(file)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
    e.target.value = ''
  }

  return (
    <>
      <div
        className={cn(
          'flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg p-8 cursor-pointer transition-colors',
          isDragOver ? 'border-primary bg-primary/5' : 'hover:bg-muted/50',
        )}
        onClick={() => inputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <p className="text-sm font-medium pointer-events-none">{label}</p>
        <p className="text-xs text-muted-foreground pointer-events-none">{sublabel}</p>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={handleChange}
      />
    </>
  )
}
